import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Toaster, toast } from "sonner";
import { Crosshair, Upload, ScanLine, CheckCircle2, Plus, Trash2, ShieldCheck, Send, FileText, Sparkles, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColor = {
  intake: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  adas_analysis: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  approved: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  in_calibration: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  completed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  paid: "bg-emerald-600/30 text-emerald-200 border-emerald-500/40",
};

export default function PublicSubmit() {
  const { token } = useParams();
  const [shop, setShop] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [tab, setTab] = useState("submit");

  const [form, setForm] = useState({
    vin: "", customer_name: "", customer_phone: "", insurance_company: "",
    claim_number: "", vehicle_year: "", vehicle_make: "", vehicle_model: "",
    contact_name: "", contact_email: "", notes: "",
  });
  const [lines, setLines] = useState([]);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInputRef = React.useRef(null);

  // ─── Auto-Quote state (Iteration 29 → 30 checklist) ───
  const [catalog, setCatalog] = useState(null);         // { services, default_discount_pct, shop_name }
  const [selectedOps, setSelectedOps] = useState({});   // { key: true }
  const [applyDiscount, setApplyDiscount] = useState(false);

  // Load services catalog once
  React.useEffect(() => {
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/public/body-shop/${token}/services-catalog`)
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog({ services: [], default_discount_pct: 0, shop_name: "" }));
  }, [token]);

  const toggleOp = (key) => setSelectedOps((s) => ({ ...s, [key]: !s[key] }));

  const selectedList = React.useMemo(() => {
    if (!catalog?.services) return [];
    return catalog.services.filter((s) => selectedOps[s.key]);
  }, [catalog, selectedOps]);

  const retailSubtotal = selectedList.reduce((a, s) => a + s.retail_price, 0);
  const discountPct = catalog?.default_discount_pct || 0;
  const finalTotal = applyDiscount && discountPct > 0
    ? retailSubtotal * (1 - discountPct / 100)
    : retailSubtotal;
  const savings = retailSubtotal - finalTotal;

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) return toast.error("File too large (max 12MB)");
    setUploadLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/body-shop/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const d = r.data;
      setForm((f) => ({
        ...f,
        vin: d.vin || f.vin,
        customer_name: d.customer_name || f.customer_name,
        insurance_company: d.insurance_company || f.insurance_company,
        claim_number: d.claim_number || f.claim_number,
        vehicle_year: d.vehicle_year || f.vehicle_year,
        vehicle_make: d.vehicle_make || f.vehicle_make,
        vehicle_model: d.vehicle_model || f.vehicle_model,
      }));
      setLines(d.lines || []);
      if (d.file_id) {
        setUploadedFileId(d.file_id);
        setUploadedFileName(file.name);
      }
      if (d._warning) {
        toast.warning(`AI fallback: ${d._warning.substring(0, 80)}`);
      } else {
        toast.success(`Parsed ${d.lines?.length || 0} lines from ${file.name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/public/body-shop/${token}`)
      .then((r) => { setShop(r.data.shop); setSubmissions(r.data.submissions); })
      .catch((e) => setError(e.response?.data?.detail || "Invalid link"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const runOCR = async () => {
    setOcrLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/body-shop/${token}/ocr`, { file_name: "estimate.pdf" });
      const d = r.data;
      setForm((f) => ({
        ...f,
        vin: d.vin || f.vin,
        customer_name: d.customer_name || f.customer_name,
        insurance_company: d.insurance_company || f.insurance_company,
        claim_number: d.claim_number || f.claim_number,
      }));
      setLines(d.lines || []);
      toast.success(`OCR detected ${d.lines?.length || 0} estimate lines`);
    } catch (e) {
      toast.error("OCR failed");
    } finally {
      setOcrLoading(false);
    }
  };

  const addLine = () =>
    setLines([...lines, { line_no: lines.length + 1, operation: "LBR", description: "", part_number: "", hours: 0, amount: 0 }]);

  const updateLine = (idx, key, val) => {
    const next = [...lines];
    next[idx][key] = key === "amount" || key === "hours" ? parseFloat(val) || 0 : val;
    setLines(next);
  };

  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.vin || form.vin.length < 11) return toast.error("VIN required (min 11 chars)");
    if (!form.customer_name) return toast.error("Customer name required");
    setSending(true);
    try {
      const payload = {
        ...form,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null,
        contact_email: form.contact_email || null,
        estimate_lines: lines,
        estimate_file_id: uploadedFileId || null,
      };
      const r = await axios.post(`${BACKEND_URL}/api/public/body-shop/${token}/submit`, payload);
      setSubmitted(r.data);
      toast.success("Estimate submitted successfully");
      setForm({ vin: "", customer_name: "", customer_phone: "", insurance_company: "", claim_number: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", contact_name: form.contact_name, contact_email: form.contact_email, notes: "" });
      setLines([]);
      setUploadedFileId(null);
      setUploadedFileName(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#030712] flex items-center justify-center text-slate-400">Loading...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <Crosshair className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Invalid Submission Link</h1>
          <p className="text-sm text-slate-400 mt-3">This link is not valid or has been revoked. Please contact TrueLine for a new submission URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100">
      <Toaster position="top-right" theme="dark" richColors />

      {/* Header */}
      <header className="border-b border-slate-800/80 bg-[#050a17] backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center glow-blue">
              <Crosshair className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight">TrueLine</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-slate-500 font-bold">Body Shop Submission Portal</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-slate-400">Secured · No login required</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Welcome */}
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1120] to-[#050a17] p-8">
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-400 font-bold mb-3">Welcome, partner</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">{shop.name}</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-lg leading-relaxed">
            Submit ADAS calibration jobs directly to TrueLine. Your dedicated link auto-routes work to our team — we run the OCR, analysis, and OEM compliance, then send you the commission.
          </p>
          <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
            <Stat label="Total Jobs" value={shop.job_count || 0} />
            <Stat label="Submissions" value={submissions.length} />
            <Stat label="Status" value="Active" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800/60">
          {[
            { k: "submit", l: "Submit Estimate" },
            { k: "history", l: `My Submissions (${submissions.length})` },
            { k: "invoices", l: "Invoices" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`public-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-xs uppercase tracking-wide font-bold transition-colors border-b-2 -mb-px ${
                tab === t.k ? "text-blue-400 border-blue-500" : "text-slate-400 border-transparent hover:text-slate-200"
              }`}
            >{t.l}</button>
          ))}
        </div>

        {tab === "submit" && (
          <div className="space-y-6 animate-slide-up">
            {/* Real file upload */}
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-5">
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-400 mb-1">Upload estimate (recommended)</div>
                <div className="text-sm text-slate-300">Upload a PDF, PNG, or JPG of your CCC/Mitchell/Audatex estimate — AI extracts vehicle, customer, and line items automatically.</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => uploadFile(e.target.files?.[0])}
                className="hidden"
                data-testid="public-file-input"
              />
              <Button data-testid="public-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploadLoading} className="w-full bg-blue-600 hover:bg-blue-500 h-11">
                <FileText className="w-4 h-4 mr-2" />{uploadLoading ? "AI parsing — 5-10s..." : "Upload PDF or Image"}
              </Button>
              <div className="text-xs text-slate-500 mt-2 text-center">Max 8MB · Uses OpenAI Vision for OCR</div>
            </div>

            {/* Quick OCR (mock) */}
            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-400 mb-1">Demo auto-fill</div>
                <div className="text-sm">No file? Try a synthetic sample to see the workflow.</div>
              </div>
              <Button data-testid="public-ocr-btn" onClick={runOCR} disabled={ocrLoading} variant="outline" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 shrink-0">
                <ScanLine className="w-4 h-4 mr-2" />{ocrLoading ? "Parsing..." : "Demo auto-fill"}
              </Button>
            </div>

            {/* Vehicle */}
            <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">1. Vehicle</div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">VIN *</Label>
                <Input data-testid="public-vin" className="mt-1.5 bg-[#030712] border-slate-800 font-mono" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} placeholder="17-char VIN" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Year</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" type="number" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Make</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" value={form.vehicle_make} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Model</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} /></div>
              </div>
            </div>

            {/* Customer & insurance */}
            <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">2. Customer & insurance</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Customer name *</Label><Input data-testid="public-customer" className="mt-1.5 bg-[#030712] border-slate-800" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Customer phone</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Insurance company</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" value={form.insurance_company} onChange={(e) => setForm({ ...form, insurance_company: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Claim #</Label><Input className="mt-1.5 bg-[#030712] border-slate-800 font-mono" value={form.claim_number} onChange={(e) => setForm({ ...form, claim_number: e.target.value })} /></div>
              </div>
            </div>

            {/* Estimate lines */}
            <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">3. Estimate lines</div>
                <Button data-testid="public-add-line" size="sm" variant="outline" onClick={addLine} className="border-slate-700"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
              </div>
              {lines.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8 border border-dashed border-slate-800 rounded-lg">
                  No lines yet. Click <strong>Auto-fill</strong> above or <strong>+ Add</strong> manually.
                </div>
              ) : (
                <div className="space-y-2">
                  {lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-2 bg-[#030712] border-slate-800 text-xs" placeholder="Op" value={l.operation} onChange={(e) => updateLine(i, "operation", e.target.value)} />
                      <Input className="col-span-5 bg-[#030712] border-slate-800 text-xs" placeholder="Description" value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)} />
                      <Input className="col-span-2 bg-[#030712] border-slate-800 text-xs font-mono" placeholder="Hours" type="number" step="0.1" value={l.hours} onChange={(e) => updateLine(i, "hours", e.target.value)} />
                      <Input className="col-span-2 bg-[#030712] border-slate-800 text-xs font-mono" placeholder="$" type="number" step="0.01" value={l.amount} onChange={(e) => updateLine(i, "amount", e.target.value)} />
                      <button onClick={() => removeLine(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <div className="text-right text-xs text-slate-500 pt-3 border-t border-slate-800 mt-3">
                    Subtotal: <span className="font-mono text-emerald-400 ml-2">${lines.reduce((s, l) => s + (l.amount || 0), 0).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Contact + notes */}
            <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">4. Your contact (optional)</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Your name</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Your email</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Notes</Label>
                <Textarea className="mt-1.5 bg-[#030712] border-slate-800" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Anything we should know..." />
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════
                5. Instant Estimate — Service Checklist
                ═══════════════════════════════════════════════════ */}
            <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-600/5 p-6 space-y-4" data-testid="bs-auto-quote-block">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                5. Selecione os serviços / Select the services you need
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Marque os serviços que o veículo precisa — a estimativa aparece em tempo real com o desconto negociado com o seu consultor.
              </p>

              {!catalog ? (
                <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-400 mx-auto" /></div>
              ) : catalog.services.length === 0 ? (
                <div className="text-xs text-slate-400 rounded-md border border-slate-800 bg-slate-950/40 p-3">
                  Ainda não configuramos a tabela de preços para o seu portal. Fale com o seu consultor.
                </div>
              ) : (
                <>
                  {/* Checklist grouped by category */}
                  <div className="space-y-4">
                    {(catalog.categories || []).map((cat) => {
                      const items = catalog.services.filter((s) => s.category === cat.key);
                      if (items.length === 0) return null;
                      return (
                        <div key={cat.key}>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{cat.en_name} / {cat.pt_name}</div>
                          <div className="grid gap-1.5">
                            {items.map((s) => (
                              <label
                                key={s.key}
                                data-testid={`bs-svc-${s.key}`}
                                className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition ${
                                  selectedOps[s.key]
                                    ? "border-emerald-500/50 bg-emerald-500/10"
                                    : "border-slate-800 bg-slate-950 hover:border-slate-600"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!selectedOps[s.key]}
                                  onChange={() => toggleOp(s.key)}
                                  className="accent-emerald-500 w-4 h-4"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium text-slate-100">
                                    {s.name_en} <span className="text-slate-500">· {s.name_pt}</span>
                                  </div>
                                  <div className="text-[10px] text-slate-500 truncate">{s.short_en}</div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="font-mono text-base font-bold text-slate-200">${s.retail_price.toFixed(2)}</div>
                                  <div className="text-[9px] text-slate-600 uppercase tracking-wider">{s.unit_en}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary + discount toggle */}
                  {selectedList.length > 0 && (
                    <div className="rounded-lg bg-[#0B1120] border border-amber-500/30 p-4 space-y-3" data-testid="bs-quote-summary">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 uppercase tracking-wider font-bold">
                          {selectedList.length} serviço(s) selecionado(s)
                        </span>
                        <span className="font-mono text-slate-300">Retail: ${retailSubtotal.toFixed(2)}</span>
                      </div>

                      {discountPct > 0 && (
                        <button
                          type="button"
                          onClick={() => setApplyDiscount((v) => !v)}
                          data-testid="bs-apply-discount-btn"
                          className={`w-full rounded-lg border p-3 transition text-left ${
                            applyDiscount
                              ? "border-emerald-500/60 bg-emerald-500/15"
                              : "border-slate-700 bg-slate-950 hover:border-emerald-500/40"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              applyDiscount ? "border-emerald-500 bg-emerald-500" : "border-slate-600"
                            }`}>
                              {applyDiscount && <CheckCircle2 className="w-4 h-4 text-slate-950" />}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-emerald-300">
                                Ativar meu desconto negociado ({discountPct}% OFF)
                              </div>
                              <div className="text-[11px] text-slate-400 mt-0.5">
                                {applyDiscount
                                  ? `✓ Aplicado — você economiza $${savings.toFixed(2)}`
                                  : `Clique para aplicar o desconto acordado com seu consultor.`}
                              </div>
                            </div>
                          </div>
                        </button>
                      )}

                      <div className="border-t border-slate-800 pt-3 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total estimado</div>
                          <div className="text-[10px] text-slate-600">USD · Imposto não incluso</div>
                        </div>
                        <div className="text-3xl font-mono font-bold text-amber-300" data-testid="bs-quote-total">
                          ${finalTotal.toFixed(2)}
                        </div>
                      </div>

                      <div className="rounded-md border border-slate-800 bg-slate-950/60 p-2.5">
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          <strong className="text-slate-300">Aviso:</strong> Esta estimativa usa preços de referência. A fatura final pode variar
                          conforme os procedimentos específicos da montadora, os recursos ADAS da versão do veículo e ajustes acordados com o seu consultor.
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <Button data-testid="public-submit-btn" onClick={submit} disabled={sending} className="w-full bg-blue-600 hover:bg-blue-500 h-12 text-base">
              <Send className="w-4 h-4 mr-2" />{sending ? "Submitting..." : "Submit Estimate to TrueLine"}
            </Button>

            {submitted && (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-6 glow-blue animate-slide-up">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="font-display text-xl font-semibold">Submitted!</div>
                    <div className="text-sm text-slate-300 mt-1">
                      We received your estimate for the <strong>{submitted.preview.vehicle}</strong> (VIN <span className="font-mono text-blue-300">{submitted.preview.vin}</span>).
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">ADAS operations detected</div>
                        <div className="font-mono text-2xl text-cyan-400 mt-0.5">{submitted.preview.adas_ops_detected}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Estimated calibration fees</div>
                        <div className="font-mono text-2xl text-emerald-400 mt-0.5">${submitted.preview.estimated_cal_fees.toFixed(2)}</div>
                      </div>
                    </div>
                    {submitted.tracking_url && (
                      <a
                        data-testid="track-link"
                        href={submitted.tracking_url}
                        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-medium transition-colors"
                      >
                        Track this submission →
                      </a>
                    )}
                    <div className="text-xs text-slate-500 mt-4">
                      {submitted.email_status === "sent" || submitted.email_status === "mock_logged"
                        ? "Confirmation email sent with your tracking link."
                        : "Bookmark the tracking link above — it's your magic link to this job."}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "history" && (
          <div className="space-y-3 animate-slide-up">
            {submissions.length === 0 ? (
              <div className="text-center py-16 text-sm text-slate-500 border border-dashed border-slate-800 rounded-xl">
                <Upload className="w-10 h-10 mx-auto mb-3 opacity-50" />
                No submissions yet. Submit your first estimate to start.
              </div>
            ) : (
              submissions.map((s) => (
                <div key={s.id} data-testid={`submission-${s.id}`} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 hover:border-blue-500/40 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-blue-300">{s.vin}</div>
                      <div className="font-medium mt-1">{s.vehicle_year} {s.vehicle_make} {s.vehicle_model}</div>
                      <div className="text-xs text-slate-500 mt-1">{s.customer_name} · Claim {s.claim_number || "—"}</div>
                    </div>
                    <Badge className={`${statusColor[s.status] || statusColor.intake} border uppercase text-[10px] tracking-wider`}>{s.status.replace("_", " ")}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-800/60 text-xs">
                    <div><div className="text-slate-500">ADAS ops</div><div className="font-mono text-cyan-400 text-base mt-0.5">{s.adas_ops_count}</div></div>
                    <div><div className="text-slate-500">Cal fees</div><div className="font-mono text-emerald-400 text-base mt-0.5">${s.adas_cal_fees.toFixed(2)}</div></div>
                    <div><div className="text-slate-500">Submitted</div><div className="text-slate-300 mt-0.5">{new Date(s.created_at).toLocaleDateString()}</div></div>
                  </div>
                  {s.job_token && (
                    <a href={`/track/${s.job_token}`} className="inline-block mt-3 text-xs text-blue-400 hover:underline font-medium">
                      View tracking details →
                    </a>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {tab === "invoices" && <InvoicesTab token={token} />}

        <div className="text-center text-xs text-slate-600 pt-8">
          Powered by <span className="text-blue-400">TrueLine Calibration Platform</span> · Secure partner submission link
        </div>
      </main>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-slate-800 bg-[#050a17] p-4">
    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
    <div className="font-display text-xl font-semibold mt-1">{value}</div>
  </div>
);

function InvoicesTab({ token }) {
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [paying, setPaying] = React.useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND}/api/public/body-shop/${token}/invoices`)
      .then((r) => setInvoices(r.data))
      .finally(() => setLoading(false));
  };
  React.useEffect(load, [token]);

  const payInvoice = async (inv) => {
    setPaying(inv.id);
    try {
      const r = await axios.post(`${BACKEND}/api/payments-ext/invoices/${inv.id}/pay-with-token`, {
        token,
        success_url: `${window.location.origin}/submit/${token}?paid=${inv.id}`,
        cancel_url: window.location.href,
      });
      if (r.data.mode === "mock") {
        toast.success("Payment processed (mock mode — Stripe key not configured)");
        load();
      } else {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally {
      setPaying(null);
    }
  };

  const downloadPdf = (inv) => {
    const url = `${BACKEND}/api/payments-ext/invoices/${inv.id}/pdf?token=${token}&print=1`;
    window.open(url, "_blank");
  };

  const statusBadge = {
    draft: "bg-slate-500/20 text-slate-300 border-slate-500/30",
    sent: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    paid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    overdue: "bg-red-500/20 text-red-300 border-red-500/30",
  };

  if (loading) return <div className="text-sm text-slate-500 text-center py-12">Loading invoices...</div>;

  return (
    <div className="space-y-3 animate-slide-up">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400 mb-1">Your invoices</div>
        <div className="text-sm text-slate-300">
          View, pay, or download printable PDFs of invoices from TrueLine. Pay securely via Stripe.
        </div>
      </div>
      {invoices.length === 0 ? (
        <div className="text-center py-16 text-sm text-slate-500 border border-dashed border-slate-800 rounded-xl">
          No invoices yet
        </div>
      ) : invoices.map((inv) => (
        <div key={inv.id} data-testid={`pub-inv-${inv.id}`} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs text-blue-300">{inv.invoice_number}</div>
              <div className="text-sm mt-1">{inv.notes ? inv.notes.split("|")[0] : "TrueLine ADAS Calibration"}</div>
              <div className="text-xs text-slate-500 mt-1">Issued {new Date(inv.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-right">
              <Badge className={`${statusBadge[inv.status] || "bg-slate-700/40 text-slate-400 border-slate-700"} border uppercase text-[10px] tracking-wider`}>{inv.status}</Badge>
              <div className="font-mono text-2xl text-emerald-400 mt-2">${inv.total.toFixed(2)}</div>
            </div>
          </div>
          <div className="flex gap-2 mt-4 pt-3 border-t border-slate-800/60">
            <Button data-testid={`pdf-btn-${inv.id}`} size="sm" variant="outline" onClick={() => downloadPdf(inv)} className="border-slate-700 text-xs">
              Download PDF
            </Button>
            {inv.status !== "paid" && (
              <Button data-testid={`pay-btn-${inv.id}`} size="sm" disabled={paying === inv.id} onClick={() => payInvoice(inv)} className="bg-blue-600 hover:bg-blue-500 text-xs ml-auto">
                {paying === inv.id ? "Processing..." : "Pay with Stripe"}
              </Button>
            )}
            {inv.status === "paid" && (
              <div className="ml-auto text-xs text-emerald-400 font-medium flex items-center gap-1">
                ✓ Paid on {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : "—"}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
